import { OrderUnitOfMeasure, RateType } from '@treadinc/horizon-api-spec';

/**
 * Maps OrderUnitOfMeasure to equivalent RateType.
 * This mapping replicates the backend ORDER_UOM_TO_RATE_TYPE constant
 * from app/models/rate.rb lines 56-63.
 */
const ORDER_UOM_TO_RATE_TYPE_MAPPING: Partial<Record<OrderUnitOfMeasure, RateType>> = {
  [OrderUnitOfMeasure.HOUR]: RateType.RATE_PER_HOUR,
  [OrderUnitOfMeasure.LOAD]: RateType.RATE_PER_LOAD,
  [OrderUnitOfMeasure.TON]: RateType.RATE_PER_TON,
  [OrderUnitOfMeasure.TONNE]: RateType.RATE_PER_TONNE,
  [OrderUnitOfMeasure.YARD]: RateType.RATE_PER_YARD,
  [OrderUnitOfMeasure.BUSHEL]: RateType.RATE_PER_BUSHEL,
};

/**
 * Checks if there is a rate type mismatch between material rate UoM and customer rate type
 * when material rate itemization is disabled.
 *
 * @param customerRateType - The customer's rate type (e.g., RatePerTon, RatePerLoad)
 * @param materialRateUoM - The material rate unit of measure (e.g., Ton, Load)
 * @param materialRateItemized - Whether the material rate is itemized
 * @returns true if there is a mismatch that should trigger a warning, false otherwise
 */
export const checkRateTypeMismatch = (
  customerRateType: RateType | null | undefined,
  materialRateUoM: OrderUnitOfMeasure | string | null | undefined,
  materialRateItemized: boolean | null | undefined,
): boolean => {
  // No warning needed if any required value is missing
  if (!customerRateType || !materialRateUoM) {
    return false;
  }

  // No warning needed if material rate is itemized
  if (materialRateItemized) {
    return false;
  }

  // Get the equivalent rate type for the material UoM
  const equivalentRateType =
    ORDER_UOM_TO_RATE_TYPE_MAPPING[materialRateUoM as OrderUnitOfMeasure];

  // If there's no mapping for this UoM, we can't compare, so no warning
  if (!equivalentRateType) {
    return false;
  }

  // Return true if the rate types don't match (mismatch detected)
  return customerRateType !== equivalentRateType;
};
